/// <reference types="vite/client" />
import { useQuery } from "@tanstack/react-query";

import { programRoleCapabilitiesApi } from "../api/client";
import { parseRolePermissions } from "../lib/permissions/parseRolePermissions";
import type { WorkbenchRoleCapabilities } from "../lib/permissions/types";
import { useAuth } from "./useAuth";

export const rolePermissionsKeys = {
  all: ["role-permissions"] as const,
  detail: (roleName: string) => [...rolePermissionsKeys.all, roleName] as const,
};

/**
 * Role capabilities rarely change mid-session — the signed-in user's role
 * doesn't flip while they're working a deal — so the query is treated as
 * effectively static rather than refetched on every window focus/mount.
 */
const ROLE_PERMISSIONS_STALE_TIME = Number.POSITIVE_INFINITY;

export interface UseRolePermissionsResult {
  /** Parsed, Workbench-shaped section capabilities for the current user's role. Fails safe (every section hidden/read-only) while loading or on error. */
  capabilities: WorkbenchRoleCapabilities;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Fetches and parses the current user's Step6 role capabilities. Reads
 * `roleName` off `useAuth()` (`user.userRole`) and calls
 * `ProgramRoleCapabilitiesApi.getRolePermissions`, then converts the raw
 * flat permission list into per-section visible/writable/forcedReadOnly
 * verdicts via `parseRolePermissions` (see `src/lib/permissions`).
 *
 * Role-only: this does not account for deal status/phase/VLA-lock — pair
 * with `SectionPermissionsApi`-backed field-level checks once that work
 * starts (matrix §6.4).
 * @returns {UseRolePermissionsResult} The parsed capabilities plus query status, failing safe (all sections hidden/read-only) whenever the role is unknown, still loading, or the request errors.
 */
export function useRolePermissions(): UseRolePermissionsResult {
  const { user } = useAuth();
  const roleName = user?.userRole ?? "";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: rolePermissionsKeys.detail(roleName),
    queryFn: async ({ signal }) => {
      const response = await programRoleCapabilitiesApi.getRolePermissions(roleName, { signal });
      return response.data;
    },
    enabled: !!roleName,
    staleTime: ROLE_PERMISSIONS_STALE_TIME,
  });

  return {
    capabilities: parseRolePermissions(data),
    isLoading: !!roleName && isLoading,
    isError,
    error,
  };
}
